import { AppState } from 'src/store';
import { createSelector, OutputSelector, OutputParametricSelector } from 'reselect';
import { SortOption } from 'src/common-ui/components/CompanionListView/CompanionListView';
import { parseActions } from './utils/ConfigurableGrid.utils';
import { ClientActionHandler, ClientActionHandlersConfig, ConfigurableGridColumnDef } from './ConfigurableGrid.types';
import type { ConfigurableGridOwnProps } from 'configuration-schemas/ownProps';
import { SubheaderDropdownProps } from '../Subheader/SubheaderDropdown';
import { cloneDeep, defaultTo, get, isNil } from 'lodash';
import { BasicItem, BasicPivotItem } from 'src/worker/pivotWorker.types';
import { filterData } from 'src/utils/Pivot/Filter';
import { externalGridSearchFields } from 'src/utils/Domain/Constants';
import { ConfigurableGridGroupBySelection } from './ConfigurableGrid.slice';
import { flattenToLeaves } from 'src/utils/Pivot/Flatten';
import { AgFlatResult } from 'src/utils/Component/AgGrid/AgDataFormat';
import { generateOrderedGroups } from 'src/utils/Component/AgGrid/AgDataFormat';
import { groupedToAgFlatTree } from 'src/utils/Component/AgGrid/AgDataFormat';
import { GroupBySlice, initialSortBy } from '../Subheader/Subheader.slice';
import { getGroupBy } from '../Subheader/Subheader.selectors';
import { ConfigurableGridViewDefn } from 'configuration-schemas/viewdefns/viewdefn';
import { blacklistByDataIndex } from 'src/utils/Component/AttributeBlacklist.utils';
import { BasicViewItem } from 'configuration-schemas/viewdefns/general';

export interface SelectorSubheaderDropdownProps extends Omit<SubheaderDropdownProps, 'handleChangeOnDropdown'> {}

interface ViewDefnData {
  dependentCalcs: Record<string, any>;
  companionSortOptions: SortOption[];
  defaultCompanionSortField?: string;
  clientActionHandlers: ClientActionHandler;
}

export interface ConfigurableGridDataSelectorProps {
  showFlowStatus: boolean;
  leafId: string;
}

interface GroupByDropdownPropsSelector
  extends OutputSelector<
    AppState,
    SelectorSubheaderDropdownProps | undefined,
    (
      groupBySelection: ConfigurableGridGroupBySelection | undefined,
      dropdownConfig: SelectorSubheaderDropdownProps | undefined,
    ) => SelectorSubheaderDropdownProps | undefined
  > {}

export interface FloorsetDropdownPropsSelector
  extends OutputSelector<
    AppState,
    SelectorSubheaderDropdownProps | undefined,
    (
      floorsets: BasicItem[],
      selectedFloorset: string | undefined,
      viewDefn: ConfigurableGridViewDefn,
    ) => SelectorSubheaderDropdownProps | undefined
  > {}

interface GridDataSelector
  extends OutputParametricSelector<
    AppState,
    ConfigurableGridDataSelectorProps,
    BasicPivotItem[],
    (
      gridData: BasicPivotItem[],
      showFlowStatus: boolean,
      flowStatus: number[],
      search: string,
      viewDefn: ConfigurableGridViewDefn,
    ) => BasicPivotItem[]
  > {}

interface GridGroupedDataSelector
  extends OutputParametricSelector<
    AppState,
    ConfigurableGridDataSelectorProps,
    AgFlatResult | undefined,
    (
      gridData: BasicPivotItem[],
      showFlowStatus: boolean,
      flowStatus: number[],
      leafId: string,
      search: string,
      viewDefn: ConfigurableGridViewDefn,
      groupByDropdownProps: SelectorSubheaderDropdownProps,
    ) => AgFlatResult | undefined
  > {}

const getColumnDefs = (state: AppState): ConfigurableGridColumnDef[] | undefined => {
  return state.pages.assortmentBuild.configurableGrid.viewDefn?.columnDefs;
};

const getCompanionSortConfig = (state: AppState) => {
  return state.pages.assortmentBuild.configurableGrid.viewDefn?.companionGrid.sortConfig;
};

const getActions = (state: AppState): ClientActionHandlersConfig | undefined => {
  return state.pages.assortmentBuild.configurableGrid.viewDefn?.actions;
};

export const getGroupBySelection = (state: AppState): ConfigurableGridGroupBySelection | undefined => {
  if (isNil(getGroupByDropdownConfig(state))) {
    return undefined;
  }
  const groupBy = getGroupBy(state);
  const selectedIndex = groupBy.selection || 0;
  const option = groupBy.options[selectedIndex];
  return {
    option,
    selectedIndex,
  };
};
const getGroupByDropdownConfig = (state: AppState): SelectorSubheaderDropdownProps | undefined => {
  return state.pages.assortmentBuild.configurableGrid.viewDefn?.subheaderDropdowns.groupBy;
};

export const getFloorsetData = (state: AppState): BasicItem[] => {
  return state.subheader.topMemberOptions || [];
};

export const getSelectedFloorset = (state: AppState): string | undefined => {
  return state.subheader.topMemberSelection;
};

export const getConfigurableGridData = (state: AppState): BasicPivotItem[] => {
  return state.pages.assortmentBuild.configurableGrid.gridData;
};

const getShowFlowStatus = (_state: AppState, { showFlowStatus }: ConfigurableGridDataSelectorProps): boolean => {
  return showFlowStatus;
};

const getFlowStatus = (state: AppState): number[] => {
  return state.subheader.flowStatus;
};

const getSearch = (state: AppState): string => {
  return state.subheader.search;
};

const getRawViewDefn = (state: AppState): ConfigurableGridViewDefn => {
  return state.pages.assortmentBuild.configurableGrid.viewDefn;
};

export const getAttributeBlacklist = (state: AppState) => {
  return state.subheader.attributeBlacklist;
};

export const getCurrentDepartment = (state: AppState): string | null => {
  return state.scope.scope.productMember;
};

export const getViewDefn = createSelector(
  getRawViewDefn,
  getAttributeBlacklist,
  getCurrentDepartment,
  (viewDefn, blacklist, currentDepartment): ConfigurableGridViewDefn => {
    if (!viewDefn || !blacklist || !currentDepartment) {
      return viewDefn;
    }
    const withColumnDefs = viewDefn as ConfigurableGridViewDefn & { columnDefs?: ConfigurableGridColumnDef[] };
    return {
      ...viewDefn,
      columnDefs: withColumnDefs.columnDefs
        ? blacklistByDataIndex(withColumnDefs.columnDefs, blacklist, currentDepartment)
        : withColumnDefs.columnDefs,
      columns: viewDefn.columns
        ? blacklistByDataIndex(viewDefn.columns, blacklist, currentDepartment)
        : viewDefn.columns,
    } as ConfigurableGridViewDefn;
  },
);

const getLeafId = (state: AppState, { leafId }: ConfigurableGridDataSelectorProps) => {
  return leafId;
};

export const getLeafIdentifier = (ownProps: ConfigurableGridOwnProps) => {
  return ownProps.keys.leafIdProp ? ownProps.keys.leafIdProp : ownProps.keys.idProp;
};

export const getViewDefnData = createSelector(
  getColumnDefs,
  getCompanionSortConfig,
  getActions,
  getAttributeBlacklist,
  getCurrentDepartment,
  (columnDefs, sortConfig, actions, blacklist, currentDepartment): ViewDefnData => {
    const filteredColumnDefs =
      columnDefs && blacklist && currentDepartment
        ? (blacklistByDataIndex(
            columnDefs as unknown as BasicViewItem[],
            blacklist,
            currentDepartment,
          ) as unknown as ConfigurableGridColumnDef[])
        : columnDefs;

    const sortConfigView = sortConfig?.view as BasicViewItem[] | undefined;
    const filteredSortView =
      sortConfigView && blacklist && currentDepartment
        ? blacklistByDataIndex(sortConfigView, blacklist, currentDepartment)
        : sortConfigView;

    // generate dependent calc object
    const dependentCalcs: Record<string, any> = {};
    if (!isNil(filteredColumnDefs)) {
      filteredColumnDefs.forEach((colDef) => {
        if (colDef.dependentCalc) {
          dependentCalcs[colDef.dataIndex] = colDef.dependentCalc;
        }
      });
    }

    // generate companion view props
    const companionSortOptions = (filteredSortView || []) as SortOption[];
    const companionSortSelected: SortOption = companionSortOptions.find(
      (opt) => opt.dataIndex === sortConfig?.default,
    ) ||
      companionSortOptions[0] || { dataIndex: 'id', text: 'ID' };
    const defaultCompanionSortField = companionSortSelected.dataIndex;

    // generate actionHandlers
    const clientActionHandlers = !isNil(actions) ? parseActions(actions) : {};
    return {
      dependentCalcs,
      companionSortOptions,
      defaultCompanionSortField,
      clientActionHandlers,
    };
  },
);

export const getGroupByDropdownProps: GroupByDropdownPropsSelector = createSelector(
  getGroupBySelection,
  getGroupByDropdownConfig,
  (groupBySelection, dropdownConfig) => {
    if (isNil(dropdownConfig)) {
      return;
    }

    const configCopy = cloneDeep(dropdownConfig);
    configCopy.selection = defaultTo(groupBySelection?.selectedIndex || dropdownConfig.defaultSelection, 0);
    return configCopy;
  },
);

export const getNeededFilterInfo = createSelector(
  getFlowStatus,
  getSearch,
  getViewDefn,

  (flowStatus, search, viewDefn) => {
    return {
      flowStatus,
      search,
      viewDefn,
    };
  },
);

export const getGroupedConfigurableGridData: GridGroupedDataSelector = createSelector(
  getConfigurableGridData,
  getShowFlowStatus,
  getFlowStatus,
  getLeafId,
  getSearch,
  getViewDefn,
  getGroupByDropdownProps,
  (gridData, showFlowStatus, flowStatus, leafId, search, viewDefn, groupByDropdownProps) => {
    if (isNil(groupByDropdownProps) || isNil(groupByDropdownProps.selection)) {
      return undefined;
    }

    const configSearchOrDefafult = get(viewDefn, 'searchIndexes', externalGridSearchFields) as string[];
    const selectedGroupBy = groupByDropdownProps.options[groupByDropdownProps.selection];
    const groupByKey = selectedGroupBy.dataIndex;
    const finalFlowStatus = showFlowStatus ? flowStatus : [];
    const filteredGridData = filterData(gridData, search, configSearchOrDefafult, finalFlowStatus);
    const flatData = flattenToLeaves(filteredGridData);
    const groupedItems = generateOrderedGroups(flatData, groupByDropdownProps as GroupBySlice);
    const result = groupedToAgFlatTree(
      groupedItems,
      groupByKey,
      search,
      initialSortBy,
      flowStatus,
      groupByDropdownProps.options,
      leafId === 'id' ? 'name' : `${leafId}`,
      true,
    );

    // add in asyncstate to grouped rows for correct rendering
    const agFlatTree = result.agFlatTree.map((item) => {
      return !item.hasOwnProperty('asyncstate') ? { ...item, asyncstate: {} } : item;
    });

    return { ...result, agFlatTree };
  },
);
